import { delayOnLeave, menuAnimation, submenuAnimation } from './animations'

export const query = (selector, parent = document) => {
  return parent.querySelector(selector)
}

export const queryAll = (selector, parent = document) => {
  return Array.from(parent.querySelectorAll(selector))
}

const clamp = (val, min, max) => {
  return val > max ? max : val < min ? min : val
}

export const handleColoredTextBlockExpand = () => {
  const blocks = queryAll('.colored-text-block')

  blocks.forEach((block) => {
    const contentHeight = block.scrollHeight
    if (contentHeight <= 241) return // 241 is the height of block with 4 lines of text

    block.classList.add('expandable')

    const opener = query('.colored-text-block__top--expand', block)
    opener.addEventListener('click', () => {
      block.classList.toggle('expanded')

      if (!block.classList.contains('expanded')) {
        block.style.maxHeight = '240px'
        return
      }

      block.style.maxHeight = `${contentHeight}px`
    })
  })
}

export const setHomeLink = () => {
  const logoContainer = query('.menu__logo-container')
  const currentLang = document.documentElement.lang

  logoContainer.href = '/' + (currentLang === 'da' ? '' : currentLang)
}

export const borderOnStickyList = () => {
  const lists = queryAll('.plain__content')

  lists.forEach((list) => {
    const stickyDetect = query('.plain__sticky-detect', list)
    const anchors = query('.plain__list', list)

    const observer = new IntersectionObserver(
      ([e]) => {
        anchors.classList.toggle('sticked', e.intersectionRatio < 1)
      },
      { threshold: [0, 1] }
    )

    observer.observe(stickyDetect)
  })
}

export const christmasCardsSetup = () => {
  const cards = queryAll('.christmas-card')
  const modalBox = query('#modal-teleport')

  cards.forEach((card) => {
    const opener = query('.christmas-card--open', card) // image element in timeline

    opener.addEventListener('click', () => {
      // move popup content to modal box
      const popup = query('.christmas-card-popup', card)
      modalBox.innerHTML = popup.outerHTML

      let activePage = -1 // -1 = front image, 0 = first page, 1 = second page, etc.
      const pages = queryAll('.christmas-card-popup__pages', modalBox) // pages container
      const arrows = queryAll('.christmas-card-popup__arrows > button', modalBox) // arrow buttons
      const image = query('.christmas-card-popup__image', modalBox) // front image
      const close = query('.christmas-card-popup--close', modalBox)

      const handleArrows = (directionData) => {
        // calculate new index based on direction
        const direction = directionData === 'left' ? -1 : 1
        const newIndex = clamp(activePage + direction, -1, pages.length - 1)

        // hide previous page and show new page
        pages[activePage]?.classList.remove('active')
        activePage = newIndex
        pages[activePage]?.classList.add('active')

        // disable arrows if first or last page is active
        arrows[0].disabled = activePage === -1
        arrows[1].disabled = activePage === pages.length - 1

        // show card image if index is negative
        if (activePage === -1) {
          image.classList.remove('hidden')
        }

        // hide card image if first page is active
        if (activePage === 0) {
          image.classList.add('hidden')
        }
      }

      arrows.forEach((arrow) => {
        arrows[1].disabled = activePage === pages.length - 1 // disable right arrow if there are
        arrow.addEventListener('click', () => handleArrows(arrow.dataset.direction))
      })

      // disable scroll and show modal
      document.documentElement.classList.add('no-scroll')
      modalBox.classList.add('active')

      const handleButton = (e) => {
        switch (e.key) {
          case 'Escape':
            closePopup()
            break
          case 'ArrowLeft':
            handleArrows('left')
            break
          case 'ArrowRight':
            handleArrows('right')
            break
        }
      }

      const closePopup = () => {
        modalBox.classList.remove('active')
        modalBox.innerHTML = ''
        document.documentElement.classList.remove('no-scroll')
        window.removeEventListener('keyup', handleButton)
      }

      // close popup on [x] click
      close.addEventListener('click', closePopup)

      // allow closing popup with escape and navigation with arrows
      window.addEventListener('keyup', handleButton)
    })
  })
}

export const timelineSetup = () => {
  if (!query('.lr-timeline')) return

  // setup timeline with christmas cards
  christmasCardsSetup()

  const container = query('.timeline-container')
  const slides = queryAll('.swiper-slide')

  // calculate original width-height ration and do the same for current height
  // if width would exceed 612px (default horizontal photo width for LR portraits), crop it to 612
  // it's required to force slide width with styling to avoid scropping slides at the end
  slides.forEach((slide) => {
    const img = query('img', slide)

    const originalW = parseInt(img.getAttribute('width'))
    const originalH = parseInt(img.getAttribute('height'))

    let newW = originalH > originalW ? '50%' : '100%'
    let newMaxW = originalH > originalW ? '300px' : '600px'

    slide.style.width = newW
    slide.style.maxWidth = newMaxW

    // add padding to avoid text cropping -- skip on desktop
    const resizeSlide = () => {
      if (slide.clientWidth < 200) {
        slide.style.width = '200px'
      }

      if (window.innerWidth > 768) {
        slide.style.width = newW
        slide.style.padding = null
        return
      }

      slide.style.padding = `0 ${(slide.clientWidth - container.clientWidth) / 2}px`
    }

    window.addEventListener('resize', resizeSlide)
    resizeSlide()
  })
}

export const handleEmails = () => {
  const emailPlaceholders = queryAll('[data-email-placeholder]')

  const encodeEmail = (email) => {
    return email
      .split('')
      .map((letter) => `&#${letter.charCodeAt()};`)
      .join('')
  }

  emailPlaceholders?.forEach((el) => {
    const email = el.dataset.emailPlaceholder

    // prettier-ignore
    el.innerHTML = `<span>${encodeEmail(email)}</span><span>${encodeEmail('@')}</span><span>${encodeEmail('lassenricard.dk')}</span>`;

    if (!el.classList.contains('nolink')) {
      el.href = `javascript:sendEmail('${btoa(encodeEmail(email + '@lassenricard.dk'))}')`
    }
  })
}

export const handleAccordionExpandAll = () => {
  const expandAllButtons = queryAll('.accordion__expand-all')
  if (!expandAllButtons.length) return

  expandAllButtons.forEach((button) => {
    button.addEventListener('click', () => {
      button.classList.toggle('active')
      const shouldExpand = button.classList.contains('active')

      const accordionItems = queryAll('.accordion-item', button.parentElement)
      accordionItems.forEach((item) => {
        if (shouldExpand) item.classList.add('active')
        else item.classList.remove('active')

        const content = query('.accordion-item__content', item)
        content.style.maxHeight = shouldExpand ? content.scrollHeight + 'px' : '0px'
      })

      // finish if it was normal accordion
      if (accordionItems.length) return

      // case for employee profile accordion
      const userAccordionItems = queryAll('.accordion-user-profile')
      userAccordionItems.forEach((item) => changeUserAccordionItem(query('button', item), shouldExpand))
    })
  })
}

const changeUserAccordionItem = (button, expand = null) => {
  const cross = query('.accordion-user-profile--cross', button)
  const content = button.nextElementSibling
  const accordionContainer = button.parentElement.parentElement // employee-full__accordion-employee
  const expandAllButton = query('.accordion__expand-all', accordionContainer)
  const allItems = queryAll('.accordion-user-profile--cross', accordionContainer)

  if (expand == null) {
    // if no expand argument was given just toggle
    cross.classList.toggle('active')
  } else {
    // otherwise override the class
    if (expand) cross.classList.add('active')
    else cross.classList.remove('active')
  }

  if (cross.classList.contains('active')) {
    content.style.maxHeight = content.scrollHeight + 'px'

    // check if all accordion items are opened and toggle "expand/collapse all" button
    const allOpened = allItems.every((item) => item.classList.contains('active'))
    if (allOpened) expandAllButton.classList.add('active')
  } else {
    content.style.maxHeight = null

    // check if all accordion items are closed and toggle "expand/collapse all" button
    const allClosed = allItems.every((item) => !item.classList.contains('active'))
    if (allClosed) expandAllButton.classList.remove('active')
  }
}

export const handleProfileAccordion = () => {
  const buttons = queryAll('.accordion-user-profile__button')
  if (!buttons.length) return

  buttons.forEach((button) => {
    button.addEventListener('click', () => changeUserAccordionItem(button))
  })
}

export const sizeEmployees = () => {
  const attachEvent = () => {
    const employees = queryAll('.employee')
    if (employees.length === 0) return

    const resize = () => {
      employees.forEach((e) => {
        const photo = query('.employee__photo', e)
        photo.style.height = e.offsetWidth * 1.2 + 'px'
      })
    }

    window.addEventListener('resize', resize)
    resize()
  }

  // observer changes in body to detect filtering and re-rendering of node
  const observer = new MutationObserver(attachEvent)
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })

  attachEvent()
}

export const positionDesktopMenu = () => {
  const menuItems = queryAll('.menu__item[data-level="0"]')

  const positionItems = (item) => {
    const submenuItemsContainer = query('.submenu__items', item)
    if (!submenuItemsContainer) return

    const gap = parseInt(getComputedStyle(submenuItemsContainer).gap) // get container's flex gap
    const submenuItems = queryAll('li', submenuItemsContainer) // get submenu items
    const startX = item.getBoundingClientRect().x + 10 // get parent element's X as starting point

    let contentWidth = -gap // compensate for 1 gap
    submenuItems.forEach((i) => (contentWidth += i.clientWidth + gap)) // calculate content width from submenu items & gap

    // if content would overflow the window, reduce the padding by difference in width
    // prettier-ignore
    if (contentWidth + startX >= window.innerWidth - 50) {
        const newPadding = startX - (contentWidth + startX - (window.innerWidth - 50));
        submenuItemsContainer.style.paddingLeft = (newPadding > 0 ? newPadding : 5) + "px"; // avoid setting negative padding
      } else {
        submenuItemsContainer.style.paddingLeft = startX + "px"; // otherwise set default padding
      }
  }

  menuItems.forEach((item) => {
    // position submenu items on hover & on load
    item.addEventListener('mouseenter', () => positionItems(item))
    positionItems(item)

    // re-position submenu items on resize
    window.addEventListener('resize', () => positionItems(item))

    // if any child is active, keep submenu open
    const submenuItems = query('.submenu__items', item)
    if (submenuItems?.innerHTML.includes('is-active')) {
      item.classList.add('active-submenu')
      positionItems(item)
    }
  })
}

// by default the page title is rendered to "node-title" with "display: none"
// this function moves the title to element "taget-title" if one is specified
export const moveTitle = () => {
  const title = query('.node-title')
  const targetTitle = query('.title--target')
  if (targetTitle) {
    targetTitle.innerHTML = title.innerHTML
  }
}

export const setupMenu = () => {
  // submenu extending
  queryAll('.menu__expand-toggle').forEach((el) => {
    el.addEventListener('click', () => {
      const parentList = el.parentElement.parentElement // .menu__item
      submenuAnimation(parentList, !parentList.classList.contains('is-expanded'))
      parentList.classList.toggle('is-expanded')

      const wrapper = query('.menu__wrapper')
      const menuItems = queryAll('.menu__item')
      const isExpanded = menuItems.some((el) => el.classList.contains('is-expanded'))

      if (isExpanded) {
        wrapper.classList.add('submenu-expanded')
      } else {
        wrapper.classList.remove('submenu-expanded')
      }
    })
  })

  // menu toggling on mobile
  query('.menu__hamburger').addEventListener('click', () => {
    query('.menu__wrapper').classList.toggle('expanded')
    query('html').classList.toggle('no-scroll')

    const isExpanded = query('.menu__wrapper').classList.contains('expanded')

    // collapsed every opened submenu on close
    if (!isExpanded) {
      queryAll('.menu__item').forEach((el) => {
        if (el.classList.contains('is-expanded')) {
          query('.menu__expand-toggle', el).click() // needs to be done via .click() to properly remove inlined max-height
        }
      })
    }

    menuAnimation(isExpanded)
  })

  // set active language preview on desktop
  const activeLang = query('.menu__langs--desktop .language-link.is-active')
  query('.menu__active-lang').innerHTML = activeLang.hreflang === 'da' ? 'dk' : activeLang.hreflang

  // create a delay when mouse leaves the language container or menu item
  delayOnLeave('.menu__langs-container', '.menu__langs', '.5s, .3s')
  queryAll('.menu__items > .menu__item').forEach((el) => {
    delayOnLeave(el, query('.submenu__items', el), '.3s, .1s')

    // force active submenu to the top
    const subItems = query('.submenu__items', el)
    if (subItems) {
      el.addEventListener('mouseover', () => (subItems.style.zIndex = 10))
      el.addEventListener('mouseout', () => (subItems.style.zIndex = 1))
    }
  })

  // change language name to language code on mobile
  queryAll('.menu__langs--mobile .language-link').forEach((lang) => {
    const langCode = lang.hreflang
    lang.innerHTML = langCode === 'da' ? 'dk' : langCode
  })
}

// "Show more" button handle for employees / clients
export const handleListExpand = () => {
  const button = query('.list__button')
  if (button) {
    const attachedList = query('.items-list', button.parentElement)
    const elements = queryAll('article', attachedList)

    // add class specifying list contains more than 'x' elements
    // to properly display rows and the "show more" button
    let counter = null
    if (elements.length > 2) counter = 2
    if (elements.length > 3) counter = 3
    if (elements.length > 4) counter = 4
    if (elements.length > 6) counter = 6
    if (elements.length > 8) counter = 8
    if (counter) attachedList.classList.add(`list-${counter}-items`)

    button.addEventListener('click', () => {
      button.classList.toggle('toggled')
      attachedList.classList.toggle('expanded')
    })
  }
}

// create side menu with anchors based on content
export const contentWithMenuSetup = () => {
  // check if page has "content with menu" paragraph
  const sections = queryAll('.plain__content-section.menu-item')
  if (sections.length === 0) {
    return
  }

  // add menu items based on h3
  sections.forEach((section) => {
    let header = query('h3', section).innerText
    if (header) {
      let newLi = document.createElement('li')
      newLi.className = 'plain__list-item'
      newLi.innerHTML = `<a href="#${section.id}">${header}</a>`
      query('.plain__list')?.appendChild(newLi)
    }
  })

  query('.plain__list-item')?.classList.add('active') // set first menu element as active

  // adds click events for list items
  const listItems = queryAll('.plain__list-item')
  listItems.forEach((item) => {
    item.addEventListener('click', () => {
      setActiveElement(listItems.indexOf(item))
    })
  })

  // adds scrolling-activation behavior
  sections.forEach((section) => {
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveElement(sections.indexOf(section))
          }
        })
      },
      {
        rootMargin: '-40% 0px -60% 0px',
        threshold: 0,
      }
    ).observe(section)
  })

  // underlines and darken active list element
  function setActiveElement(index) {
    query('.plain__list-item.active').classList.remove('active') // remove class from currently active element
    listItems[index].classList.add('active') // add class to clicked element
  }
}

export const handleAccordionExpand = () => {
  const buttons = queryAll('.accordion-item__title')
  if (buttons.length === 0) {
    return
  }

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      const accordionItem = button.parentElement
      const accordionContainer = accordionItem.parentElement
      const contentElement = query('.accordion-item__content', accordionItem)
      const allItems = queryAll('.accordion-item', accordionContainer)
      const expandAllButton = query('.accordion__expand-all', accordionContainer)

      accordionItem.classList.toggle('active')

      if (accordionItem.classList.contains('active')) {
        contentElement.style.maxHeight = contentElement.scrollHeight + 'px'

        // check if all accordion items are opened and toggle "expand/collapse all" button
        const allOpened = allItems.every((item) => item.classList.contains('active'))
        if (allOpened) expandAllButton.classList.add('active')
      } else {
        contentElement.style.maxHeight = '0px'

        // check if all accordion items are closed and toggle "expand/collapse all" button
        const allClosed = allItems.every((item) => !item.classList.contains('active'))
        if (allClosed) expandAllButton.classList.remove('active')
      }
    })
  })
}
